import { useCallback, useEffect, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  BackgroundVariant,
} from '@xyflow/react'
import { GRAPH_MULTISELECT_PROPS } from '../lib/graphInteractionProps'
import { RightDragPan } from './RightDragPan'
import '@xyflow/react/dist/style.css'
import { VarGetNode }        from './nodes/rule/VarGetNode'
import { RuleNotNode }       from './nodes/rule/RuleNotNode'
import { RuleCompareNode }   from './nodes/rule/RuleCompareNode'
import { RuleResultNode }    from './nodes/rule/RuleResultNode'
import { RuleAnimTimeNode }  from './nodes/rule/RuleAnimTimeNode'
import { RuleLogicNode }     from './nodes/rule/RuleLogicNode'
import { RuleTimeInStateNode } from './nodes/rule/RuleTimeInStateNode'
import { smRuntime }         from '../lib/smRuntime'
import { DeleteEdge }        from './nodes/DeleteEdge'
import { EventActionsPanel } from './EventActionsPanel'
import { GraphHeaderBar, CanvasHint, headerLabel } from './GraphHeaderBar'
import { SM_THEME as T }    from './nodes/stateMachineTheme'
import { debugState }        from '../lib/debugState'
import { dropDisplacedEdges } from '../lib/graphConnectPolicy'
import { nodeControlClass }   from '../lib/nodeControlClass.js'
import { resolveTransitionDuration } from '../lib/transitionDuration.js'
import AnchoredOverlay from './AnchoredOverlay'

const RULE_NODE_TYPES = {
  varGet:                VarGetNode,
  ruleNot:               RuleNotNode,
  ruleAnd:               RuleLogicNode,
  ruleOr:                RuleLogicNode,
  ruleTimeInState:       RuleTimeInStateNode,
  ruleCompare:           RuleCompareNode,
  ruleResult:            RuleResultNode,
  ruleAnimTimeRemaining: RuleAnimTimeNode,
  ruleAnimTimeRatio:     RuleAnimTimeNode,
}

const RULE_EDGE_TYPES = {
  default: DeleteEdge,
}

const RESULT_NODE = {
  id: 'ruleResult',
  type: 'ruleResult',
  position: { x: 460, y: 160 },
  data: { connected: false },
  deletable: false,
}

let _ruleNodeId = 1
const nextRuleId = () => `rn_${_ruleNodeId++}`

// `group` lays the toolbar out as two runs: the nodes that READ something
// (values) and the nodes that COMBINE them (logic). The right-click menu shows
// the same list in the same order.
const MENU_ITEMS = [
  { label: '+ Get Variable',        type: 'varGet',                group: 'value' },
  { label: '+ Time In State',       type: 'ruleTimeInState',       group: 'value' },
  { label: '+ Anim Time Remaining', type: 'ruleAnimTimeRemaining', group: 'value' },
  { label: '+ Anim Fraction',       type: 'ruleAnimTimeRatio',     group: 'value' },
  { label: '+ Compare',             type: 'ruleCompare',           group: 'logic' },
  { label: '+ AND',                 type: 'ruleAnd',               group: 'logic' },
  { label: '+ OR',                  type: 'ruleOr',                group: 'logic' },
  { label: '+ NOT',                 type: 'ruleNot',               group: 'logic' },
]

// ─── Inner canvas — uses useReactFlow for right-click position ────────────────
function RuleCanvas({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onAdd }) {
  const { screenToFlowPosition } = useReactFlow()
  const [contextMenu, setContextMenu] = useState(null)

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])

  useEffect(() => {
    if (!contextMenu) return
    const h = () => closeMenu()
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [contextMenu, closeMenu])

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneContextMenu={onPaneContextMenu}
        onPaneClick={closeMenu}
        nodeTypes={RULE_NODE_TYPES}
        edgeTypes={RULE_EDGE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.4 }}
        deleteKeyCode="Delete"
        style={{ background: T.canvasBg }}
        {...GRAPH_MULTISELECT_PROPS}
      >
        <Background variant={BackgroundVariant.Dots} color={T.dots} gap={20} size={1} />
        <Controls style={{ background: T.btnBg, border: `1px solid ${T.btnBorder}`, color: T.btnText }} />
        <RightDragPan />
      </ReactFlow>

      {contextMenu && (
        <AnchoredOverlay
          x={contextMenu.screenX} y={contextMenu.screenY}
          style={{
            background: 'var(--m-panel)',
            border: '1px solid var(--m-line)',
            borderRadius: 6,
            boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            zIndex: 1000,
            overflow: 'hidden',
            fontFamily: 'var(--m-font)',
            minWidth: 170,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ padding: '4px 0' }}>
            {MENU_ITEMS.map(item => (
              <div
                key={item.type}
                onClick={() => { onAdd(item.type, contextMenu.flowPos); closeMenu() }}
                style={{
                  padding: '7px 12px',
                  cursor: 'pointer',
                  color: T.btnText,
                  fontSize: 11,
                  borderLeft: `3px solid ${T.btnBorder}`,
                  margin: '3px 6px',
                  borderRadius: '0 4px 4px 0',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--m-panel-hi)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {item.label}
              </div>
            ))}
          </div>
        </AnchoredOverlay>
      )}
    </>
  )
}

// ─── TransitionRuleEditor ─────────────────────────────────────────────────────
export function TransitionRuleEditor({
  smName,
  ruleGraph,
  variables = [],
  activeClipName = null,
  onBack,
  onRuleChange,
  actions = [],
  availableCustomEvents = [],
  onActionsChange,
  transitionDuration,
  onTransitionDurationChange,
  smNodeId = null,
  sourceStateId = null,
}) {
  const seedNodes = (ruleGraph?.nodes?.length > 0) ? ruleGraph.nodes : [RESULT_NODE]
  const seedEdges = ruleGraph?.edges ?? []

  const [nodes, setNodes, onNodesChange] = useNodesState(seedNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(seedEdges)

  useEffect(() => {
    onRuleChange?.(nodes, edges)
  }, [nodes, edges]) // eslint-disable-line react-hooks/exhaustive-deps

  // Update result node connected status
  useEffect(() => {
    const connected = edges.some(e => e.target === 'ruleResult')
    setNodes(ns => ns.map(n =>
      n.id === 'ruleResult'
        ? (n.data.connected === connected ? n : { ...n, data: { ...n.data, connected } })
        : n
    ))
  }, [edges]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the live timing to inject into anim time nodes: the source state's own
  // player while it is the active state (what the rule actually reads), else
  // the clip's per-name timing.
  useEffect(() => {
    const id = setInterval(() => {
      const own = smNodeId ? debugState.smClipTimings[smNodeId] : null
      const timings = own && sourceStateId && own.stateId === sourceStateId
        ? own
        : (activeClipName ? debugState.clipTimings[activeClipName] : null)
      setNodes(ns => ns.map(n => {
        if (n.type === 'ruleAnimTimeRemaining') {
          const val = timings?.remaining ?? null
          return n.data._debugValue === val ? n : { ...n, data: { ...n.data, _debugValue: val } }
        }
        if (n.type === 'ruleAnimTimeRatio') {
          const val = timings?.ratio ?? null
          return n.data._debugValue === val ? n : { ...n, data: { ...n.data, _debugValue: val } }
        }
        if (n.type === 'ruleTimeInState') {
          // Live only while this transition's source state is the active one —
          // the clock the evaluator reads (runtime.stateEnteredAt).
          const rt = smNodeId ? smRuntime[smNodeId] : null
          const val = rt && sourceStateId && rt.currentState === sourceStateId && typeof rt.stateEnteredAt === 'number'
            ? Math.round((Date.now() - rt.stateEnteredAt) / 10) / 100
            : null
          return n.data._debugValue === val ? n : { ...n, data: { ...n.data, _debugValue: val } }
        }
        return n
      }))
    }, 100)
    return () => clearInterval(id)
  }, [activeClipName, setNodes, smNodeId, sourceStateId])

  const updateNode = useCallback((nodeId, key, value) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ))
  }, [setNodes])

  const nodesWithHandlers = nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      variables,
      activeClipName,
      onChange: (key, value) => updateNode(n.id, key, value),
    },
  }))

  const onConnect = useCallback((params) => {
    // Shared Unreal capacity rules (ENG-877) — rule graphs are pure data,
    // so this is the same replace-on-target behavior as before.
    setEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setEdges])

  const addNode = useCallback((type, position) => {
    const id = nextRuleId()
    const pos = position ?? { x: 150 + Math.random() * 100, y: 100 + Math.random() * 100 }
    if (type === 'varGet') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { varName: variables[0]?.name ?? '' } }])
    } else if (type === 'ruleNot' || type === 'ruleAnd' || type === 'ruleOr' || type === 'ruleTimeInState') {
      setNodes(ns => [...ns, { id, type, position: pos, data: {} }])
    } else if (type === 'ruleCompare') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { operator: '>', threshold: 0 } }])
    } else if (type === 'ruleAnimTimeRemaining') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { subtype: 'remaining' } }])
    } else if (type === 'ruleAnimTimeRatio') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { subtype: 'ratio' } }])
    }
  }, [setNodes, variables])

  // Header (where you are + the crossfade length), toolbar (add a node), then
  // the canvas with the Actions card floating in its top-right corner.
  return (
    <div style={{
      width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
      background: T.canvasBg, fontFamily: 'var(--m-font)',
    }}>
      <GraphHeaderBar onBack={onBack} crumbs={['Blueprint', smName, 'Transition Rule']}>
        <TransitionBlendField value={transitionDuration} onChange={onTransitionDurationChange} />
      </GraphHeaderBar>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap',
        padding: '6px 12px', background: 'var(--m-panel-hi)', borderBottom: '1px solid var(--m-line)',
      }}>
        <span style={{ ...headerLabel, marginRight: 2 }}>Add</span>
        {MENU_ITEMS.filter(i => i.group === 'value').map(item => (
          <button key={item.type} onClick={() => addNode(item.type)} style={toolBtn}>{item.label.slice(2)}</button>
        ))}
        <span style={{ width: 1, height: 18, background: 'var(--m-line)', margin: '0 4px' }} />
        {MENU_ITEMS.filter(i => i.group === 'logic').map(item => (
          <button key={item.type} onClick={() => addNode(item.type)} style={toolBtn}>{item.label.slice(2)}</button>
        ))}
      </div>

      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <ReactFlowProvider>
          <RuleCanvas
            nodes={nodesWithHandlers}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onAdd={addNode}
          />
        </ReactFlowProvider>
        <CanvasHint>Right-click to add · wire into “Can Enter Transition” to make the rule live</CanvasHint>

        {/* Actions (ENG-1062): custom events this transition fires once, when
            it is taken. The condition (the canvas) decides WHETHER to switch;
            actions are what switching DOES. */}
        <EventActionsPanel
          title="Actions"
          hint="Custom events fired once when this transition is taken."
          actions={actions}
          available={availableCustomEvents}
          onChange={onActionsChange}
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}
        />
      </div>
    </div>
  )
}

// ─── Blend panel ──────────────────────────────────────────────────────────────
// How long this transition crossfades. Both states are evaluated and blended
// for its whole length (GraphEvaluator), so it is the shape of the switch, not
// decoration — which is why it sits above Actions: duration is a property of
// switching, actions are what switching DOES.
//
// Blank = the shared 0.3s every transition used before this field existed, so
// an untouched machine is unchanged. A typed 0 is a HARD CUT and is kept
// distinct from blank — a reaction that should snap reads as a smear at 0.3.
// Rules + the 10s ceiling live in resolveTransitionDuration
// (src/lib/transitionDuration.js).
function TransitionBlendField({ value, onChange }) {
  return (
    <label
      title="How long this transition crossfades. Blank uses the 0.3 s default; 0 is a hard cut."
      style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
    >
      <span style={headerLabel}>Blend</span>
      <input
        type="number" min={0} max={10} step={0.05}
        className={nodeControlClass()}
        value={value ?? ''}
        placeholder="0.3"
        onChange={e => onChange?.(e.target.value === ''
          ? null
          // Through the SAME resolver the chip and the runtime use, so the
          // panel cannot display a value the crossfade would not honour —
          // typing 50 showed 50 here while both of those used the 10s cap.
          : resolveTransitionDuration({ transitionDuration: e.target.value }))}
        style={{
          width: 64, background: 'var(--m-bg)', border: '1px solid var(--m-line)',
          color: 'var(--m-fg)', padding: '4px 6px', borderRadius: 'var(--m-radius-sm)',
          fontSize: 11, fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
        }}
      />
      <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>s</span>
    </label>
  )
}

const toolBtn = {
  background: 'var(--m-node-bg)', border: '1px solid var(--m-line)', color: 'var(--m-fg)',
  borderRadius: 'var(--m-radius-sm)', padding: '3px 9px', fontSize: 11,
  fontFamily: 'var(--m-font)', cursor: 'pointer', whiteSpace: 'nowrap',
}
